/**
 * Snowbird car-shipping lanes between Cape Coral, Florida and the major
 * northern/inland metros that send vehicles south every winter.
 *
 * Each origin city is authored once with genuinely local detail (where a
 * carrier can legally load, what the corridor looks like, when the lane
 * tightens). Two directional pages are derived from every origin:
 *
 *   /snowbird-routes/<city>-to-cape-coral      (southbound)
 *   /snowbird-routes/cape-coral-to-<city>      (northbound)
 */

import { ORIGINS_A } from "./snowbird-origins/a";
import { ORIGINS_B } from "./snowbird-origins/b";
import { ORIGINS_C } from "./snowbird-origins/c";
import { ORIGINS_D } from "./snowbird-origins/d";

export type MeetSpot = { name: string; note: string };

export type SnowbirdOrigin = {
  /** URL slug for the city, e.g. "minneapolis-mn". */
  slug: string;
  city: string;
  state: string;
  abbr: string;
  /** One-line metro descriptor used on hub cards. */
  metro: string;
  /** Driving miles to Cape Coral, FL. */
  miles: number;
  /** Typical door-to-door transit, e.g. "3–5 days". */
  transitDays: string;
  /** The interstates a carrier actually runs on this lane. */
  corridor: string;
  /** 2–3 paragraphs about the lane itself. */
  intro: string[];
  /** Paragraphs specific to shipping city -> Cape Coral. */
  southbound: string[];
  /** Paragraphs specific to shipping Cape Coral -> city. */
  northbound: string[];
  /** Real places in the origin metro where a full-size carrier can load. */
  meetSpots: MeetSpot[];
  /** Areas of the metro we cover on this lane. */
  areas: string[];
  /** Seasonal timing guidance. */
  timing: string[];
  /** What drives price on this lane. */
  priceNote: string;
  faqs: { q: string; a: string }[];
};

export const SNOWBIRD_ORIGINS: SnowbirdOrigin[] = [
  ...ORIGINS_A,
  ...ORIGINS_B,
  ...ORIGINS_C,
  ...ORIGINS_D,
]
  .filter((o, i, all) => all.findIndex((x) => x.slug === o.slug) === i)
  .sort((a, b) => a.city.localeCompare(b.city));

export type LaneDirection = "southbound" | "northbound";

export type SnowbirdLane = {
  slug: string;
  direction: LaneDirection;
  origin: SnowbirdOrigin;
  fromLabel: string;
  toLabel: string;
  title: string;
  description: string;
};

function laneFor(origin: SnowbirdOrigin, direction: LaneDirection): SnowbirdLane {
  const cityLabel = `${origin.city}, ${origin.abbr}`;
  const capeLabel = "Cape Coral, FL";
  const southbound = direction === "southbound";
  return {
    slug: southbound ? `${origin.slug}-to-cape-coral` : `cape-coral-to-${origin.slug}`,
    direction,
    origin,
    fromLabel: southbound ? cityLabel : capeLabel,
    toLabel: southbound ? capeLabel : cityLabel,
    title: southbound
      ? `Car Shipping from ${origin.city} to Cape Coral, FL | Snowbird Auto Transport`
      : `Car Shipping from Cape Coral, FL to ${origin.city}, ${origin.abbr} | Auto Transport`,
    description: southbound
      ? `Ship a car from ${origin.city}, ${origin.state} to Cape Coral, Florida — ${origin.miles.toLocaleString()} miles, roughly ${origin.transitDays} in transit, with real pickup points, seasonal timing and door-to-door delivery on Cape Coral canal streets.`
      : `Ship a car from Cape Coral, Florida back to ${origin.city}, ${origin.state} — ${origin.miles.toLocaleString()} miles via ${origin.corridor}, spring snowbird scheduling, open and enclosed carriers and a free quote.`,
  };
}

export const SNOWBIRD_LANES: SnowbirdLane[] = SNOWBIRD_ORIGINS.flatMap((origin) => [
  laneFor(origin, "southbound"),
  laneFor(origin, "northbound"),
]);

export const SNOWBIRD_LANE_BY_SLUG = new Map(SNOWBIRD_LANES.map((l) => [l.slug, l]));

export function getSnowbirdLane(slug: string) {
  return SNOWBIRD_LANE_BY_SLUG.get(slug);
}

/** Where a carrier can realistically load or unload around Cape Coral. */
export const CAPE_CORAL_MEET_SPOTS: MeetSpot[] = [
  {
    name: "Your driveway or the swale in front of it",
    note: "On the wide arterials — Del Prado, Santa Barbara, Skyline, Chiquita, Veterans, Pine Island Road — a full 75-foot rig loads at the curb with no repositioning. Most Cape Coral pickups never leave the address.",
  },
  {
    name: "Publix at Coralwood Center, Del Prado Blvd S",
    note: "Central, lit, and easy to reach from either bridge. Park along the outer lot edge away from the storefront and the driver can drop the ramps without blocking traffic.",
  },
  {
    name: "Cape Coral Sports Complex / Park lot, Pine Island Rd",
    note: "Large, flat, and directly off the truck route into the north Cape. Our first choice when the home address sits on a canal cul-de-sac with a 30-foot turning radius.",
  },
  {
    name: "Target / Publix plaza at Santa Barbara & Veterans",
    note: "Geographic center of the city, minutes from Palmetto-Pine, Sandoval and the Trafalgar corridor. Good winter-season staging point when a driver is running several Cape stops.",
  },
  {
    name: "Home Depot on Cape Coral Pkwy near the Midpoint Bridge",
    note: "Best option for the south Cape — Cape Harbour, Tarpon Point, Yacht Club, Savona — where the residential blocks are narrow and lined with parked boat trailers.",
  },
  {
    name: "Merchants Crossing, North Fort Myers",
    note: "Just across the Caloosahatchee, right off US 41. Where we meet Matlacha, Pine Island and Burnt Store customers when a bridge weight or width limit rules out a door attempt.",
  },
];

export const CAPE_CORAL_ACCESS_NOTES = [
  "Cape Coral has more than 400 miles of canals, and the streets that follow them are narrow, dead-ended and often lined with parked boat trailers. A 75-to-80-foot carrier physically cannot turn around on many of them.",
  "Gated communities — Sandoval, Entrada, Cape Royal, Coral Lakes, Bella Vida, Tuscany Isles — want the carrier on the vendor log before it reaches the callbox. Give us the community name when you book and we clear it in advance.",
  "The Cape Coral Bridge and Midpoint Bridge are tolled; the Caloosahatchee (US 41) bridge is not. Carriers coming from I-75 usually route Colonial to Veterans or Pine Island Road, which is why north-Cape addresses often get an earlier arrival window than the south end.",
  "Season matters on the ground too: between January and March the Cape's arterials carry double their off-season traffic, so drivers prefer early-morning windows before 10 a.m.",
];
